import { Box, Flex, HStack, Skeleton, Stack } from "@chakra-ui/react";
import type { BoxProps } from "@chakra-ui/react";
import { Tooltip } from "@/components/ui/tooltip";
import Show from "@/components/Molecules/Show";
import FaceliftText from "@/components/Atoms/FaceliftText/FaceliftText";
import { FaceliftScrollContainer } from "@/components/Atoms/FaceliftScrollContainer";
import { useColorMode } from "@/components/ui/color-mode-utils";
import { colors } from "@/components/ui/theme";
import { useState, useMemo } from "react";
import { invoiceChartColors } from "@/constants/colors";

export interface ProductData {
  name: string;
  amount: string;
  value: number;
}

export interface InvoiceChartProps extends BoxProps {
  title?: string;
  data: ProductData[];
  isLoading?: boolean;
  showLegend?: boolean;
}

export const InvoiceChart = ({ title = "", data, isLoading = false, showLegend = false, ...rest }: InvoiceChartProps) => {
  const skeletonItems = Array.from({ length: 5 }, (_, i) => i);
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  const [disabledSeries, setDisabledSeries] = useState<Set<string>>(new Set());

  const visibleData = useMemo(() => {
    return data.filter(item => !disabledSeries.has(item.name));
  }, [data, disabledSeries]);

  const maxValue = Math.max(1, ...visibleData.map((d) => d.value));

  const handleLegendClick = (seriesName: string) => {
    setDisabledSeries((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(seriesName)) {
        newSet.delete(seriesName);
      } else {
        newSet.add(seriesName);
      }
      return newSet;
    });
  };

  // Pick a nice step so three ticks cover the max with a bit of headroom
  const pickNiceStep = (max: number): number => {
    const candidates = [100, 250, 500, 1000, 1500, 2000, 2500, 3000, 5000, 7500, 10000];
    const target = Math.ceil(max * 1.05);
    for (const s of candidates) {
      if (s * 3 >= target) return s;
    }
    const magnitude = 10 ** Math.floor(Math.log10(target));
    return Math.ceil(target / (3 * magnitude)) * magnitude;
  };

  const allSeriesDisabled = visibleData.length === 0;

  const step = pickNiceStep(maxValue);
  const top = step * 3;
  const ticks = [0, step, step * 2, step * 3];
  const labels = ticks.map((n) => `$${n.toLocaleString()}`);

  const labelWidthPx = 82; // left label column
  const gapPx = 3.943; // space between label and bar
  const barMaxWidthPx = 161; // maximum bar width
  const startX = labelWidthPx + gapPx; // inner container start for bars

  // Vertical grid lines at tick positions (include 0 for left baseline)
  const gridLeftPx = ticks.map((t) => startX + (t / top) * barMaxWidthPx);

  const computeWidth = (v: number) => Math.round((Math.min(v, top) / top) * barMaxWidthPx);

  return (
    <Box
      width="100%"
      height="264px"
      borderRadius="6px"
      borderWidth="1px"
      borderColor={"gray.400"}
      p={3}
      _dark={{ bg: "darkModeBgColor" }}
      _light={{ bg: "white" }}
      display="flex"
      flexDirection="column"
      {...rest}
    >
      <Show when={!!title && !isLoading}>
        <FaceliftText
          fontSize="md"
          fontWeight="semibold"
          mb={2}
          mt={1}
          w="100%"
          textAlign="center"
          _dark={{ color: "white" }}
          _light={{ color: "black" }}
        >
          {title}
        </FaceliftText>
      </Show>

      <Show when={!!isLoading}>
        <Box w="100%" flex={1} display="flex" alignItems="center" justifyContent="center" px={2}>
          <Box w="308px" minH="191px" display="flex" flexDirection="column" justifyContent="center" gap={3}>
            <Skeleton height="12px" width="180px" mb={5} ml={8} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
            <Box display="flex" flexDirection="column" gap={3}>
              {skeletonItems.map((i) => (
                <Box key={i} display="flex" alignItems="center" gap={2}>
                  <Skeleton height="12px" width="82px" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
                  <Skeleton height="19px" width={`${40 + (i % 5) * 20}px`} _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
                  <Skeleton height="12px" width="48px" _dark={{ bg: 'gray.700' }} _light={{ bg: 'gray.200' }} />
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Show>

      <Show when={!isLoading}>
        <FaceliftScrollContainer
          width="100%"
          height="191px"
          minWidth="308px"
          showScrollbarY={false}
        >
          <Box
            w="100%"
            display="flex"
            justifyContent={showLegend ? "space-between" : "center"}
            alignItems="center"
            gap={{ base: 2, md: 1, lg: 0 }}
            position="relative"
            minH="191px"
          >
            <Show when={allSeriesDisabled && showLegend}>
              <Box flex="1" display="flex" alignItems="center" justifyContent="center">
                <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                  No data found
                </FaceliftText>
              </Box>
            </Show>

            <Show when={allSeriesDisabled && !showLegend}>
              <Box position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex={10}>
                <FaceliftText fontSize="md" fontWeight="semibold" _dark={{ color: 'gray.400' }} _light={{ color: 'gray.500' }}>
                  No data found
                </FaceliftText>
              </Box>
            </Show>

            <Show when={!allSeriesDisabled}>
              <Box
                as="section"
                role="img"
                aria-label="Invoice amounts by product chart"
                position="relative"
                w="308px"
                minW="280px"
                h="191px"
                flexShrink={0}
              >
                <HStack position="absolute" left="103px" top={2} w="195px" gap="30px" justifyContent="flex-end">
                  {labels.map((l) => (
                    <FaceliftText key={l} fontSize="10px" _light={{ color: 'black' }} _dark={{ color: 'white' }}>
                      {l}
                    </FaceliftText>
                  ))}
                </HStack>

                <Box position="absolute" left={4} top="31px" w="291px" h="151px">
                  {gridLeftPx.map((left, i) => (
                    <Box
                      key={`${left}-${i}`}
                      position="absolute"
                      left={`${left}px`}
                      top="0.594px"
                      w="1px"
                      h="151px"
                      bg={i === 0 ? 'black' : 'gray.100'}
                      _dark={{ bg: i === 0 ? 'gray.400' : 'gray.600' }}
                      _light={{ bg: i === 0 ? 'gray.700' : 'gray.300' }}
                      zIndex={i === 0 ? 3 : 0}
                      aria-hidden
                    />
                  ))}

                  <Box role="table">
                    <Flex direction="column" gap="14.5px">
                      {visibleData.map((item) => (
                        <HStack key={item.name} gap="3.943px">
                          <FaceliftText
                            w="82px"
                            fontSize="11.83px"
                            fontWeight={500}
                            lineHeight="100%"
                            letterSpacing="0"
                            _light={{ color: 'black' }}
                            _dark={{ color: 'white' }}
                          >
                            {item.name}
                          </FaceliftText>
                          <Tooltip
                            content={
                              <Box
                                px={3}
                                py={2}
                                bg="white"
                                borderRadius="6px"
                                border="1px solid"
                                borderColor="gray.200"
                                boxShadow="lg"
                                _dark={{ bg: 'darkModeBgColor', borderColor: '#3a3a3a' }}
                              >
                                <FaceliftText
                                  fontSize="sm"
                                  fontWeight="600"
                                  mb={1.5}
                                  _light={{ color: 'black' }}
                                  _dark={{ color: 'white' }}
                                >
                                  {item.name}
                                </FaceliftText>
                                <FaceliftText
                                  fontSize="xs"
                                  _light={{ color: 'gray.700' }}
                                  _dark={{ color: 'gray.300' }}
                                >
                                  Amount: {item.amount}
                                </FaceliftText>
                              </Box>
                            }
                            contentProps={{
                              bg: "transparent",
                              border: "none",
                              borderRadius: "0",
                              padding: "0",
                              boxShadow: "none",
                            }}
                            positioning={{ placement: "top" }}
                            openDelay={0}
                            closeDelay={0}
                          >
                            <Box
                              h="19px"
                              bg="#117964" /* fallback */
                              bgGradient="linear-gradient(91.56deg, #1C4B74 0.38%, #117964 100.38%)"
                              borderTopRightRadius="1.972px"
                              borderBottomRightRadius="1.972px"
                              position="relative"
                              zIndex={2}
                              w={`${computeWidth(item.value)}px`}
                              aria-label={`${item.name} invoice amount: ${item.amount}`}
                              cursor="pointer"
                              transition="opacity 0.2s"
                              _hover={{ opacity: 0.85 }}
                            />
                          </Tooltip>
                          <FaceliftText
                            fontSize="12px"
                            _light={{ color: 'black' }}
                            _dark={{ color: 'white' }}
                            position="relative"
                            zIndex={4}
                          >
                            {item.amount}
                          </FaceliftText>
                        </HStack>
                      ))}
                    </Flex>
                  </Box>
                </Box>
              </Box>
            </Show>

            <Show when={showLegend}>
              <Box width="120px" display="flex" alignItems="center" justifyContent="flex-start" flexShrink={0}>
                <Stack direction="column" justify="center" align="flex-start" gap={2}>
                  {data.map((item, index) => {
                    const isDisabled = disabledSeries.has(item.name);
                    return (
                      <Stack
                        key={item.name}
                        direction="row"
                        align="center"
                        gap={1.5}
                        cursor="pointer"
                        onClick={() => handleLegendClick(item.name)}
                        opacity={isDisabled ? 0.4 : 1}
                        transition="opacity 0.2s"
                        _hover={{ opacity: isDisabled ? 0.6 : 0.8 }}
                      >
                        <Box
                          width={2.5}
                          height={2.5}
                          borderRadius="full"
                          bg={invoiceChartColors[index % invoiceChartColors.length]}
                        />
                        <FaceliftText
                          fontSize="2xs"
                          fontWeight="medium"
                          lineHeight="tight"
                          _dark={{ color: 'gray.200' }}
                          _light={{ color: 'black' }}
                          whiteSpace="nowrap"
                          textDecoration={isDisabled ? 'line-through' : 'none'}
                        >
                          {item.name}
                        </FaceliftText>
                      </Stack>
                    );
                  })}
                </Stack>
              </Box>
            </Show>
          </Box>
        </FaceliftScrollContainer>
      </Show>
    </Box>
  );
};

export default InvoiceChart;


